import { useState } from "react";
import { deleteAccount } from "../services/session.service.js";

/**
 * Type-to-confirm dialog for account deletion, mirroring mobile's
 * `promptDialogDestructiveConfirmText` — a plain OK/Cancel is too easy to fire by muscle memory
 * for something this irreversible.
 *
 * What the server actually does (roo-server § "User deletion — tombstone erasure"): revokes every
 * bank connection at the provider, then deletes the user inside a transaction that cascades to all
 * child rows, leaving only a tombstone carrying the original id and a peppered email hash. There is
 * no undo, so the copy says so plainly rather than softening it.
 */

const CONFIRM_WORD = "delete";

export function DeleteAccountDialog(p: { onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const canConfirm = typed.trim().toLowerCase() === CONFIRM_WORD;

  async function onConfirm() {
    if (!canConfirm || isBusy) return;
    setError(null);
    setIsBusy(true);
    const result = await deleteAccount();
    if (!result.ok) {
      setError(result.message);
      setIsBusy(false);
      return;
    }
    // On success the session ends and App swaps to the login page — nothing to close.
  }

  return (
    // `role="dialog"` + aria-modal so it reads as a dialog to assistive tech. Rendered inline rather
    // than portalled: there is exactly one, and a portal host would be scaffolding for nothing.
    <div className="overlay" data-testid="delete-account-dialog" role="dialog" aria-modal="true">
      <div className="card">
        <h2>Delete account</h2>
        <p className="hint">
          This permanently deletes your account and disconnects every bank connection.{" "}
          <strong>It cannot be undone.</strong>
        </p>

        <label htmlFor="confirmDelete">
          Type <strong>{CONFIRM_WORD}</strong> to confirm
        </label>
        <input
          id="confirmDelete"
          data-testid="delete-account-confirm-input"
          type="text"
          autoFocus
          autoComplete="off"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={isBusy}
        />

        <button
          type="button"
          className="danger"
          data-testid="delete-account-confirm"
          onClick={onConfirm}
          disabled={!canConfirm || isBusy}
        >
          {isBusy ? "Deleting…" : "Delete my account"}
        </button>
        <button
          type="button"
          className="link"
          data-testid="delete-account-cancel"
          onClick={p.onClose}
          disabled={isBusy}
        >
          Cancel
        </button>

        {error ? (
          <p className="error" role="alert" data-testid="delete-account-error">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
